import { CommandType } from '@shared/constants/CommandValues';
import { IEditProfileContainer } from '@shared/models/deviceProfiles/IEditProfileContainer';
import { useMemo } from 'react';

interface IFilterProfileConfigurations {
    profile: IEditProfileContainer;
    filter: string;
    selectedCommandType?: CommandType | null;
}

/**
 * Filtered views of the profile's macros, keybindings and hotkeys.
 *
 * These must stay *derived*. Mirroring them into `useState` via an effect renders one pass
 * behind the store, and that stale pass re-writes the value of whichever field is being
 * edited — which moves the caret to the end of the input on every keystroke.
 */
const useFilterProfileConfigurations = ({
    profile,
    filter,
    selectedCommandType = '' as CommandType,
}: IFilterProfileConfigurations) => {
    const filterNormalized = filter?.toLowerCase().trim();
    const macros = profile?.macros;
    const commandKeyMappings = profile?.commandKeyMappings;
    const hotkeys = profile?.hotkeys;

    const filteredMacros = useMemo(
        () =>
            macros?.filter(
                (m) =>
                    m.commands.some(
                        (c) =>
                            (c.commandType === selectedCommandType || !selectedCommandType) &&
                            (c.command?.toLowerCase().includes(filterNormalized) || !filterNormalized)
                    ) ||
                    m.name?.toLowerCase().includes(filterNormalized) ||
                    !filterNormalized
            ) ?? [],
        [macros, filterNormalized, selectedCommandType]
    );

    const filteredKeybinds = useMemo(
        () =>
            commandKeyMappings?.filter((m) =>
                m.commands.some(
                    (c) =>
                        (c.commandType == selectedCommandType || !selectedCommandType) &&
                        (c.command?.toLowerCase().includes(filterNormalized) || !filterNormalized)
                )
            ) ?? [],
        [commandKeyMappings, filterNormalized, selectedCommandType]
    );

    const filteredHotkeys = useMemo(
        () =>
            hotkeys?.filter(
                (m) =>
                    m.command?.toLowerCase().includes(filterNormalized) ||
                    m.displayName?.toLowerCase().includes(filterNormalized) ||
                    !filterNormalized
            ) ?? [],
        [hotkeys, filterNormalized]
    );

    if (!profile) return { filteredMacros: null, filteredKeybinds: [], filteredHotkeys: [] };
    return { filteredMacros, filteredKeybinds, filteredHotkeys };
};

export default useFilterProfileConfigurations;
